import { format } from 'date-fns';
import { isArray } from 'lodash';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useSWRImmutable from 'swr/immutable';

import { ISO_DATE_FORMAT, MODE_AIR, MODE_WATER, USER_ACCESS_FEATURES } from '@/lib/constants';
import { MarketRateDictionaryModel, MarketRateQueryModel } from '@/lib/models/rates/types';
import { locationsState } from '@/state/locations';
import {
  getMarketRatesByRouteState,
  getMarketRatesSearchingState,
  marketRateResultsState,
  marketRateSearchingState,
} from '@/state/rates';
import { SearchingState } from '@/types';
import { MarketRatesApiResponse } from '@/types/api-response-types';
import { PricingTimelineApiInterface, PricingValueApiInterface } from '@/types/api-types';
import { request } from '@/utils/api/request';
import { canAccessFeature } from '@/utils/auth';
import { getCurrentDateWithMinusXDays } from '@/utils/format/date-time';
import { createMarketRatesSearchUrl } from '@/utils/helpers/api-urls';
import { logEvent } from '@/utils/logger';
import { createMarketRatesKey } from '@/utils/rates';

export interface UseMarketRateProps extends MarketRateQueryModel {
  fromDay?: number;
  toDay?: number;
}

const extractTimelineData = (timeline: PricingTimelineApiInterface) => {
  const dataObject = timeline.data;
  const seriesKey = Object.keys(dataObject)[0];
  const { data, currency } = dataObject[seriesKey];

  return {
    data,
    currency: currency || 'usd',
  };
};

const extractTimelineRateValue = (
  timeline: PricingTimelineApiInterface,
  firstValue = false,
): PricingValueApiInterface => {
  const { data: nestedData, currency } = extractTimelineData(timeline);
  const datakey = firstValue ? Object.keys(nestedData).shift() : Object.keys(nestedData).pop();
  const selectedItem = datakey ? nestedData[datakey] : undefined;
  let selectedValue = 0;

  if (selectedItem && 'value' in selectedItem && selectedItem.value) {
    selectedValue = selectedItem.value;
  }

  if (isArray(selectedItem)) {
    [selectedValue] = selectedItem;
  }

  return {
    value: selectedValue,
    currency: currency || 'usd',
  };
};

const buildRequestUrl = ({ ports, mode, additionalFields = {}, fromDay, toDay }: UseMarketRateProps) => {
  const dateFrom = fromDay && getCurrentDateWithMinusXDays(fromDay);
  const dateTo = toDay && getCurrentDateWithMinusXDays(toDay);

  return createMarketRatesSearchUrl({
    ports,
    mode,
    dateFrom: dateFrom ? format(dateFrom, ISO_DATE_FORMAT) : '',
    dateTo: dateTo ? format(dateTo, ISO_DATE_FORMAT) : '',
    additionalFields,
  });
};

export const useMarketRates = ({ ports, mode, additionalFields = {}, fromDay = 30, toDay = 1 }: UseMarketRateProps) => {
  const { data: session } = useSession();
  const canAccessRates = canAccessFeature(USER_ACCESS_FEATURES.RATES, session);

  const { pol, pod } = ports[0] || {};
  const { fullKey, additionalFieldsKey } = createMarketRatesKey({ ports, additionalFields });

  const setRateResults = useSetRecoilState(marketRateResultsState);
  const setSearchingState = useSetRecoilState(marketRateSearchingState);
  const setLocations = useSetRecoilState(locationsState);

  const searchingState = useRecoilValue(getMarketRatesSearchingState({ key: fullKey }));
  const rates = useRecoilValue(getMarketRatesByRouteState({ pol, pod, additionalFieldsKey }));

  const hasPorts = !!ports.length;
  const shouldFetchRates = canAccessRates && (mode === MODE_WATER || mode === MODE_AIR);

  const requestUrl =
    hasPorts && shouldFetchRates
      ? buildRequestUrl({
          ports,
          mode,
          additionalFields,
          fromDay,
          toDay,
        })
      : null;

  const { data: ratesResponse, isLoading } = useSWRImmutable<MarketRatesApiResponse>(requestUrl, request, {
    errorRetryCount: 1,
    onError: (error) => {
      logEvent.error('Error fetching market rates', error);
    },
  });

  useEffect(() => {
    const portStates = ports.reduce(
      (accum, pair) => {
        const { fullKey: searchingStateKey } = createMarketRatesKey({ ports: [pair], additionalFields });

        return {
          ...accum,
          [searchingStateKey]: { isSearching: isLoading, hasSearched: true },
        };
      },
      {} as Record<string, SearchingState>,
    );

    setSearchingState((prevValue) => {
      return {
        ...prevValue,
        ...portStates,
      };
    });
  }, [isLoading, ratesResponse]);

  useEffect(() => {
    if (ratesResponse?.data?.responses?.length) {
      const { responses, timelines, locations } = ratesResponse.data;

      // Run through responses to create a cached dictionary of market rates with keys
      const rateDictionary = responses.reduce((accum, response) => {
        const { fullKey: cacheKey, additionalFieldsKey: additionalFieldsCacheKey } = createMarketRatesKey({
          ports: [{ pol: response.key.fromId, pod: response.key.toId }],
          additionalFields,
        });
        const indexKey = response.timelineIndex;
        const timeline = timelines[indexKey];

        if (timeline) {
          return {
            ...accum,
            [cacheKey]: {
              firstValue: extractTimelineRateValue(timeline, true),
              lastValue: extractTimelineRateValue(timeline),
              pol: response.key.fromId,
              pod: response.key.toId,
              additionalFieldsKey: additionalFieldsCacheKey,
              ...timeline,
            },
          };
        }
        return accum;
      }, {} as MarketRateDictionaryModel);

      // Merge Rates
      setRateResults((prevValue) => {
        return {
          ...prevValue,
          ...rateDictionary,
        };
      });
      // Merge Locations
      setLocations((prev) => ({ ...prev, ...locations }));
    }
  }, [ratesResponse]);

  return {
    isSearching: searchingState?.isSearching || false,
    rates,
  };
};
